import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Trophy,
  ChevronRight,
  Lightbulb,
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useConfetti } from '../../../hooks/cabin/useConfetti';

/**
 * WordHunt — Sentence-level word hunt exercise
 *
 * phase.content schema:
 * { sentences: [{ text_tr, text_en, target_words: [string], hint_tr, hint_en }],
 *   time_limit_seconds: 60 }
 */
export default function WordHunt({
  phase,
  onPhaseComplete,
  'data-testid': testId = 'word-hunt',
}) {
  const { t, lang } = useLanguage();
  const { play } = useSoundEffects();
  const { fire, fireSmall } = useConfetti();
  const shouldReduce = useReducedMotion();

  const rawSentences = phase?.content?.sentences;
  // Stabilize: `|| []` would create a new array each render and invalidate
  // startGame / handleNext useCallback dependencies unnecessarily.
  const sentences = useMemo(() => rawSentences || [], [rawSentences]);
  const TIME_LIMIT = phase?.content?.time_limit_seconds || 60;
  const maxXP = phase?.xp_in_phase || 0;

  const [gameState, setGameState] = useState('ready'); // ready | playing | finished
  const [currentIndex, setCurrentIndex] = useState(0);
  const [foundWords, setFoundWords] = useState([]); // all found across session
  const [selectedWord, setSelectedWord] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [resultCorrect, setResultCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintVisible, setHintVisible] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);

  const timerRef = useRef(null);

  const currentSentence = sentences[currentIndex];
  const sentenceText =
    lang === 'tr'
      ? currentSentence?.text_tr
      : currentSentence?.text_en || currentSentence?.text_tr;
  const hintText =
    lang === 'tr'
      ? currentSentence?.hint_tr
      : currentSentence?.hint_en || currentSentence?.hint_tr;
  const targetWords = useMemo(
    () => currentSentence?.target_words || [],
    // currentIndex drives currentSentence — use as primitive dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentIndex]
  );

  // Tokenize sentence preserving punctuation positions
  const tokens = sentenceText
    ? sentenceText.split(/(\s+)/).flatMap((part, i) => {
        if (/^\s+$/.test(part)) return [{ type: 'space', text: part, key: `s-${i}` }];
        // Split word from trailing punctuation
        const match = part.match(/^([A-Za-z'-]+)([\W]*)$/);
        if (match) {
          const arr = [{ type: 'word', text: match[1], clean: match[1], key: `w-${i}` }];
          if (match[2]) arr.push({ type: 'punct', text: match[2], key: `p-${i}` });
          return arr;
        }
        return [{ type: 'other', text: part, key: `o-${i}` }];
      })
    : [];

  const startGame = useCallback(() => {
    setGameState('playing');
    setCurrentIndex(0);
    setFoundWords([]);
    setScore(0);
    setTimeLeft(TIME_LIMIT);
    setSelectedWord(null);
    setShowResult(false);
    setHintUsed(false);
    setHintVisible(false);
    setHintPenalty(0);
    play('click');
  }, [TIME_LIMIT, play]);

  // Timer
  useEffect(() => {
    if (gameState !== 'playing') return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setGameState('finished');
          play('success');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [gameState, play]);

  const handleWordClick = useCallback(
    (token) => {
      if (showResult || gameState !== 'playing' || token.type !== 'word') return;
      play('click');
      setSelectedWord(token.clean);

      const isTarget = targetWords.some(
        (tw) => tw.toLowerCase() === token.clean.toLowerCase()
      );
      setResultCorrect(isTarget);
      setShowResult(true);

      if (isTarget) {
        play('correct');
        if (!shouldReduce) fireSmall({ y: 0.5 });
        const pts = 10 - hintPenalty;
        setScore((s) => s + Math.max(pts, 2));
        setFoundWords((fw) => [...fw, token.clean]);
      } else {
        play('wrong');
      }
    },
    [showResult, gameState, play, targetWords, shouldReduce, fireSmall, hintPenalty]
  );

  const handleNext = useCallback(() => {
    const next = currentIndex + 1;
    if (next >= sentences.length) {
      clearInterval(timerRef.current);
      setGameState('finished');
      play('success');
      if (!shouldReduce) fire({ particleCount: 80 });
    } else {
      setCurrentIndex(next);
      setSelectedWord(null);
      setShowResult(false);
      setHintUsed(false);
      setHintVisible(false);
      setHintPenalty(0);
    }
  }, [currentIndex, sentences.length, play, shouldReduce, fire]);

  const handleHint = useCallback(() => {
    if (hintUsed) return;
    setHintUsed(true);
    setHintVisible(true);
    setHintPenalty(3); // -3 points if hint used
    play('click');
  }, [hintUsed, play]);

  // Ready
  if (gameState === 'ready') {
    return (
      <div
        className="flex flex-col gap-5 w-full max-w-2xl mx-auto"
        data-testid={`${testId}-ready`}
      >
        <div className="bg-white border-2 border-[var(--border-light)] rounded-2xl p-7 text-center shadow-[0_4px_0_0_rgba(0,0,0,0.06)]">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Search size={28} className="text-amber-600" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-black text-[var(--text-main)] mb-2">
            {t('cabin.exercise.wordHunt.title')}
          </h2>
          <p className="text-sm text-[var(--text-muted)] mb-5">
            {sentences.length} {t('cabin.exercise.wordHunt.sentences')}
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left text-sm text-amber-800 space-y-1.5">
            <p className="font-bold mb-2">{t('cabin.exercise.wordHunt.howToPlay')}</p>
            <p>• {t('cabin.exercise.wordHunt.rule1')}</p>
            <p>• {t('cabin.exercise.wordHunt.rule2')}</p>
            <p>• {t('cabin.exercise.wordHunt.rule3').replace('{time}', TIME_LIMIT)}</p>
          </div>
        </div>

        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.02 }}
          whileTap={shouldReduce ? {} : { scale: 0.98 }}
          onClick={startGame}
          className="w-full py-4 bg-amber-500 text-white font-bold text-lg rounded-xl shadow-[0_4px_0_0_#d97706] hover:bg-amber-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
          data-testid={`${testId}-start`}
        >
          {t('cabin.exercise.wordHunt.startBtn')}
        </motion.button>
      </div>
    );
  }

  // Finished
  if (gameState === 'finished') {
    const total = sentences.length;
    const found = foundWords.length;
    const passed = score >= total * 5;
    const finalScore = maxXP > 0 ? Math.round((found / total) * maxXP) : score;

    return (
      <motion.div
        initial={shouldReduce ? {} : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center gap-5 w-full max-w-2xl mx-auto text-center py-4"
        data-testid={`${testId}-finish`}
      >
        <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center shadow-[0_4px_0_0_rgba(0,0,0,0.08)]">
          <Trophy size={36} className="text-amber-600" aria-hidden="true" />
        </div>

        <div>
          <h2 className="text-2xl font-black text-[var(--text-main)] mb-1">
            {t('cabin.exercise.wordHunt.finished')}
          </h2>
          <p className="text-sm text-[var(--text-muted)]">
            {found} / {total} {t('cabin.exercise.wordHunt.wordsFound')}
          </p>
        </div>

        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl px-8 py-4">
          <p className="text-4xl font-black text-amber-600">{score}</p>
          <p className="text-sm text-amber-700 font-semibold mt-1">
            {t('cabin.exercise.wordHunt.points')}
          </p>
        </div>

        {passed ? (
          <p className="text-emerald-600 font-semibold text-sm">
            {t('cabin.exercise.wordHunt.foundAll')}
          </p>
        ) : (
          <p className="text-amber-600 font-semibold text-sm">
            {t('cabin.exercise.wordHunt.keepPracticing')}
          </p>
        )}

        <motion.button
          whileHover={shouldReduce ? {} : { scale: 1.02 }}
          whileTap={shouldReduce ? {} : { scale: 0.98 }}
          onClick={() =>
            onPhaseComplete({
              score: Math.min(finalScore, maxXP),
              maxScore: maxXP,
              answers: { found, total, score },
              timeSpent: TIME_LIMIT - timeLeft,
            })
          }
          className="flex items-center gap-2 px-8 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          data-testid={`${testId}-complete`}
        >
          {t('exercise.finish')}
          <ChevronRight size={18} aria-hidden="true" />
        </motion.button>
      </motion.div>
    );
  }

  // Playing
  const timePct = (timeLeft / TIME_LIMIT) * 100;
  const timeColor =
    timeLeft > TIME_LIMIT * 0.5
      ? 'bg-emerald-500'
      : timeLeft > TIME_LIMIT * 0.2
      ? 'bg-amber-500'
      : 'bg-rose-500';

  return (
    <div
      className="flex flex-col gap-4 w-full max-w-2xl mx-auto"
      data-testid={`${testId}-playing`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
            <Search size={16} className="text-amber-600" aria-hidden="true" />
          </div>
          <span className="text-sm font-bold text-[var(--text-muted)]">
            {currentIndex + 1} / {sentences.length}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-full">
            <Trophy size={13} className="text-amber-600" aria-hidden="true" />
            <span className="text-xs font-black text-amber-700">{score}</span>
          </div>
          <div
            className={`flex items-center gap-1 font-bold text-sm ${
              timeLeft <= 10 ? 'text-rose-500' : 'text-[var(--text-muted)]'
            }`}
          >
            <Clock size={14} aria-hidden="true" />
            {timeLeft}s
          </div>
        </div>
      </div>

      {/* Timer bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden border border-gray-300">
        <motion.div
          className={`h-full rounded-full transition-all ease-linear ${timeColor}`}
          animate={{ width: `${timePct}%` }}
          transition={{ duration: 1, ease: 'linear' }}
        />
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-[var(--brand-primary)] rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex) / sentences.length) * 100}%` }}
        />
      </div>

      {/* Hint & target hint area */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-xs text-amber-800 font-semibold">
            {t('cabin.exercise.wordHunt.find')}:{' '}
            <span className="font-black">
              {targetWords[0] || '?'}
            </span>
          </p>
        </div>
        {hintText && (
          <button
            onClick={handleHint}
            disabled={hintUsed}
            className="flex items-center gap-1 px-3 py-2 text-xs font-bold bg-yellow-100 border border-yellow-300 text-yellow-700 rounded-xl hover:bg-yellow-200 transition-colors disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-400"
          >
            <Lightbulb size={13} aria-hidden="true" />
            {t('cabin.exercise.wordHunt.hint')}
          </button>
        )}
      </div>

      {/* Hint reveal */}
      <AnimatePresence>
        {hintVisible && hintText && (
          <motion.div
            initial={shouldReduce ? {} : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 text-sm text-yellow-800"
            role="note"
          >
            <span className="font-bold">{t('exercise.hint')}:</span> {hintText}
            <span className="text-xs ml-2 text-yellow-600">(-3 pts)</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sentence card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={shouldReduce ? {} : { opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
          className="bg-white border-2 border-[var(--border-light)] rounded-2xl p-5 shadow-[0_4px_0_0_rgba(0,0,0,0.06)]"
        >
          <p className="text-xs text-[var(--text-muted)] mb-3">
            {t('cabin.exercise.wordHunt.sentenceLabel').replace(
              '{n}',
              String(currentIndex + 1)
            ).replace('{total}', String(sentences.length))}
          </p>

          <div
            className="flex flex-wrap gap-1.5 leading-relaxed"
            role="group"
            aria-label={t('cabin.exercise.wordHunt.find')}
          >
            {tokens.map((token) => {
              if (token.type === 'space') return <span key={token.key}> </span>;
              if (token.type !== 'word')
                return (
                  <span key={token.key} className="text-[var(--text-muted)]">
                    {token.text}
                  </span>
                );

              const isTarget = targetWords.some(
                (tw) => tw.toLowerCase() === token.clean.toLowerCase()
              );
              const isSelected = selectedWord?.toLowerCase() === token.clean.toLowerCase();

              let cls =
                'px-2.5 py-1.5 rounded-lg font-medium text-sm cursor-pointer hover:bg-sky-50 hover:border-[var(--brand-primary)] border-2 border-transparent transition-colors bg-gray-100 text-[var(--text-main)] min-h-[36px] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--brand-primary)]';

              if (showResult) {
                if (isTarget)
                  cls = 'px-2.5 py-1.5 rounded-lg font-bold text-sm border-2 border-[#10B981] bg-[#10B981]/10 text-emerald-700 min-h-[36px]';
                else if (isSelected)
                  cls = 'px-2.5 py-1.5 rounded-lg font-bold text-sm border-2 border-rose-400 bg-rose-50 text-rose-700 min-h-[36px]';
                else
                  cls = 'px-2.5 py-1.5 rounded-lg text-sm border-2 border-transparent bg-gray-100 text-gray-400 min-h-[36px]';
              }

              return (
                <button
                  key={token.key}
                  onClick={() => handleWordClick(token)}
                  disabled={showResult}
                  className={cls}
                  data-testid={`${testId}-word-${token.clean}`}
                >
                  {token.text}
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={shouldReduce ? {} : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className={`mt-4 p-3 rounded-xl flex items-start gap-2 ${
                  resultCorrect
                    ? 'bg-[#10B981]/10 border border-[#10B981]'
                    : 'bg-rose-50 border border-rose-300'
                }`}
                role="alert"
              >
                {resultCorrect ? (
                  <CheckCircle
                    size={16}
                    className="text-[#10B981] shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                ) : (
                  <XCircle size={16} className="text-rose-500 shrink-0 mt-0.5" aria-hidden="true" />
                )}
                <span
                  className={`text-sm font-bold ${
                    resultCorrect ? 'text-emerald-700' : 'text-rose-700'
                  }`}
                >
                  {resultCorrect
                    ? `${t('exercise.correct')} +${Math.max(10 - hintPenalty, 2)} pts`
                    : `${t('exercise.wrong')} — ${t('cabin.exercise.wordHunt.correctWas')}: ${
                        targetWords[0] || ''
                      }`}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>

      {/* Next button */}
      <AnimatePresence>
        {showResult && (
          <motion.button
            initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            whileHover={shouldReduce ? {} : { scale: 1.02 }}
            whileTap={shouldReduce ? {} : { scale: 0.98 }}
            onClick={handleNext}
            className="w-full flex items-center justify-center gap-2 py-3 bg-amber-500 text-white font-bold rounded-xl shadow-[0_3px_0_0_#d97706] hover:bg-amber-600 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
            data-testid={`${testId}-next`}
          >
            {currentIndex < sentences.length - 1
              ? t('cabin.exercise.wordHunt.nextSentence')
              : t('cabin.exercise.wordHunt.seeResults')}
            <ChevronRight size={18} aria-hidden="true" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
